import { CellMeasurerCache } from "react-virtualized";
import { observable } from "mobx";
import { ObservableQuery } from "@apollo/client";
import { AccommodationSearch_Search } from "@graphql";
import { GroupedVirtualSearchManager } from "@search/GroupedVirtualSearchManager";
import { Filter, Row, parseItems } from "./utils";
import { AccommodationFilterManager } from "./AccommodationFilterManager";

import { logSubscriptionError } from "@utils/log-subscription-error";
export type AccommodationSearchStoreOpts = {
  initialFilter: Partial<Filter>;
};

export class AccommodationSearchStore {
  filterManager: AccommodationFilterManager;
  searchManager: GroupedVirtualSearchManager<Row>;

  @observable total = 0;

  query: ObservableQuery<
    AccommodationSearch_Search.Return,
    AccommodationSearch_Search.Variables
  >;

  cellMeasurerCache = new CellMeasurerCache({
    fixedWidth: true,
    defaultHeight: 75,
    minHeight: 50,
    keyMapper: (index) => this.searchManager.keyMapper(index)
  });

  constructor(options: AccommodationSearchStoreOpts) {
    this.searchManager = new GroupedVirtualSearchManager<Row>(this.fetch, true);
    this.filterManager = new AccommodationFilterManager(
      options.initialFilter,
      this.fetch
    );

    this.searchManager.fetch();

    (window as any).accommodationSearchStore = this;
  }

  init = () => {
    this.query = AccommodationSearch_Search.funcWatch({
      input: this.filterManager?.filter,
      limit: 20,
      skip: 0
    });

    this.subscribe();
  };

  subscribe = () => {
    this.query.subscribe({
      next: ({ data }) => {
        this.searchManager.merge(parseItems(data.getAccommodations));
        this.total = data.getAccommodationsCount?.total;
        this.cellMeasurerCache.clearAll();
        this.searchManager.iLoaderForceUpdate();
      },
      error: logSubscriptionError("AccommodationSearchStore")
    });
  };

  fetch = (skip?: number, limit?: number) => {
    const _skip = skip == null ? 0 : skip;
    const _limit = limit == null ? 20 : limit;

    const isNew = !skip;

    isNew && this.searchManager.clear();
    isNew && this.searchManager.setGroup(0);

    if (isNew) {
      this.init();
      return Promise.resolve();
    } else {
      return this.query.fetchMore({
        variables: {
          skip: _skip,
          limit: _limit,
          input: this.filterManager.filter
        },
        updateQuery: (previousQueryResult, options) => {
          return {
            getAccommodations: [
              ...previousQueryResult.getAccommodations,
              ...options.fetchMoreResult.getAccommodations
            ],
            getAccommodationsCount: {
              ...options.fetchMoreResult.getAccommodationsCount
            }
          };
        }
      });
    }
  };
}
